import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Terminal, ShieldCheck, FileText, ArrowRight, Loader2, Check,
  AlertCircle, Lock, ChevronLeft,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.12 — Public, token-gated e-signature page.
// Embeds the unsigned PDF + collects 3 affirmation checkboxes + typed-name
// signature. On submit, records IP/UA/timestamp server-side, regenerates the
// signed PDF with audit stamp, emails both parties.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

export default function SignAgreement() {
  usePageview("/sign-agreement");
  const { analystId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const [state, setState] = useState({ loading: true, error: null, preview: null });
  const [form, setForm] = useState({
    typed_name: "",
    affirm_read: false,
    affirm_authorised: false,
    affirm_binding: false,
    abn: "",
    address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!analystId || !token) {
      setState({ loading: false, error: "Missing token. Open the signing link from your email.", preview: null });
      return;
    }
    fetch(`${BACKEND}/api/public/agreement/${analystId}/preview?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || "Link invalid or expired.");
        return r.json();
      })
      .then((p) => {
        setState({ loading: false, error: null, preview: p });
        // Pre-populate the typed name to encourage exact-match (still editable)
        setForm((f) => ({ ...f, typed_name: f.typed_name || (p.name || "") }));
      })
      .catch((e) => setState({ loading: false, error: e.message, preview: null }));
  }, [analystId, token]);

  const allTicked = form.affirm_read && form.affirm_authorised && form.affirm_binding;

  const submit = async (e) => {
    e.preventDefault();
    if (!allTicked) {
      toast.error("Please tick all three affirmation boxes.");
      return;
    }
    if (form.typed_name.trim().length < 2) {
      toast.error("Please type your full legal name.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/agreement/${analystId}/sign?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Signing failed");
      setDone(true);
      toast.success("Agreement signed. Countersigned PDF is being emailed to you now.");
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <Wrap>
        <div className="flex items-center gap-3 text-zinc-400 mt-20">
          <Loader2 className="w-5 h-5 animate-spin text-[#FFB000]" />
          <span className="font-mono text-xs">Loading your agreement…</span>
        </div>
      </Wrap>
    );
  }

  if (state.error) {
    return (
      <Wrap>
        <div
          className="mt-12 border border-[#FF6B47]/40 bg-[#FF6B47]/[0.06] p-6"
          data-testid="sign-agreement-error"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#FF6B47] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-bold text-lg">Signing link not valid.</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{state.error}</p>
              <p className="text-sm text-zinc-500 mt-3 leading-relaxed">
                Signing links expire after 30 days. Email <a href="mailto:hello@daraos.ai" className="text-[#FFB000] hover:underline">hello@daraos.ai</a> to request a fresh one.
              </p>
            </div>
          </div>
        </div>
      </Wrap>
    );
  }

  if (state.preview?.already_signed) {
    return (
      <Wrap>
        <div className="mt-12 border border-[#00E676]/40 bg-[#00E676]/[0.05] p-6" data-testid="sign-agreement-already-signed">
          <Check className="w-7 h-7 text-[#00E676]" />
          <h1 className="font-heading font-black text-3xl mt-3 tracking-tight">Already signed.</h1>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            This agreement was already signed on{" "}
            <strong className="text-white">{new Date(state.preview.signed_at).toLocaleString()}</strong>.
            A countersigned copy was emailed to <strong className="text-white">{state.preview.email}</strong> at the time.
            If you can't find it, contact <a href="mailto:hello@daraos.ai" className="text-[#FFB000] hover:underline">hello@daraos.ai</a>.
          </p>
        </div>
      </Wrap>
    );
  }

  if (done) {
    return (
      <Wrap>
        <div className="mt-12 text-center" data-testid="sign-agreement-success">
          <div className="inline-flex p-3 border border-[#00E676]/40 bg-[#00E676]/10 mb-5">
            <Check className="w-6 h-6 text-[#00E676]" />
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            You're officially in.
          </h1>
          <p className="text-base text-zinc-400 mt-4 leading-relaxed max-w-md mx-auto">
            Countersigned PDF is on its way to <strong className="text-white">{state.preview.email}</strong>.
            You can log into <Link to="/login" className="text-[#FFB000] hover:underline">daraos.ai/login</Link> with that email — your earnings dashboard lives under <strong>Refer & Earn</strong>.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/login"
              data-testid="sign-success-login"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              LOG IN TO DASHBOARD
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </Wrap>
    );
  }

  const p = state.preview;
  return (
    <Wrap>
      <div className="mt-6" data-testid="sign-agreement-page">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 flex items-center gap-2">
          <Lock className="w-3 h-3" />
          // PRIVATE · ELECTRONIC SIGNATURE · DARA TECHNOLOGY PTY LTD
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
          Welcome, {p.first_name}.
        </h1>
        <p className="text-sm sm:text-base text-zinc-400 mt-3 leading-relaxed max-w-2xl">
          Below is your DARA Expert Independent Contractor Agreement, personalised with your details.
          Take 5 minutes to read it. When you're ready, scroll to the bottom, tick the three affirmation
          boxes, type your full legal name, and submit.
        </p>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Pill label="TIER" value={(p.tier || "standard").toUpperCase()} accent="#FFB000" />
          <Pill label="CODE" value={p.code || "—"} />
          <Pill label="EMAIL" value={p.email} />
          <Pill label="COUNTRY" value={p.country || "—"} />
        </div>

        {/* Embedded PDF */}
        <div className="mt-6 border border-zinc-800 bg-zinc-950" data-testid="sign-agreement-pdf-frame">
          <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#FFB000]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                AGREEMENT · DRAFT
              </span>
            </div>
            <a
              href={`${BACKEND}/api/public/agreement/${analystId}/pdf?token=${encodeURIComponent(token)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="sign-agreement-pdf-open"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] inline-flex items-center gap-1"
            >
              OPEN IN NEW TAB →
            </a>
          </div>
          <iframe
            title="DARA Expert Agreement"
            src={`${BACKEND}/api/public/agreement/${analystId}/pdf?token=${encodeURIComponent(token)}#view=FitH`}
            className="w-full bg-white"
            style={{ height: "78vh", minHeight: "560px" }}
          />
        </div>

        {/* Signature form */}
        <form
          onSubmit={submit}
          data-testid="sign-agreement-form"
          className="mt-6 border border-zinc-800 bg-zinc-950/40 p-5 sm:p-6 space-y-5"
        >
          <div>
            <h2 className="font-heading font-black text-xl tracking-tight">Sign electronically</h2>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Legally binding under the Electronic Transactions Act 1999 (Cth) when paired with the
              affirmation checkboxes and an audit trail.
            </p>
          </div>

          {/* Optional contractor fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="ABN / TAX ID (OPTIONAL)">
              <input
                value={form.abn}
                onChange={(e) => setForm((f) => ({ ...f, abn: e.target.value }))}
                data-testid="sign-abn"
                placeholder="e.g. 53 004 085 616"
                className={inputCls}
              />
            </Field>
            <Field label="MAILING ADDRESS (OPTIONAL)">
              <input
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                data-testid="sign-address"
                placeholder="Street, city, country"
                className={inputCls}
              />
            </Field>
          </div>

          {/* Affirmation checkboxes */}
          <div className="space-y-2">
            <Affirm
              testid="affirm-read"
              checked={form.affirm_read}
              onChange={(v) => setForm((f) => ({ ...f, affirm_read: v }))}
              label="I have read and understood the entire Agreement above, including the Schedules."
            />
            <Affirm
              testid="affirm-authorised"
              checked={form.affirm_authorised}
              onChange={(v) => setForm((f) => ({ ...f, affirm_authorised: v }))}
              label="I have full authority to enter into this Agreement on my own behalf."
            />
            <Affirm
              testid="affirm-binding"
              checked={form.affirm_binding}
              onChange={(v) => setForm((f) => ({ ...f, affirm_binding: v }))}
              label="I understand that signing electronically below creates a legally binding contract between me and Dara Technology Pty Ltd."
            />
          </div>

          {/* Typed name */}
          <Field label="TYPE YOUR FULL LEGAL NAME *">
            <input
              required
              minLength={2}
              maxLength={120}
              value={form.typed_name}
              onChange={(e) => setForm((f) => ({ ...f, typed_name: e.target.value }))}
              data-testid="sign-typed-name"
              placeholder={p.name}
              className={`${inputCls} font-mono`}
            />
            <span className="font-mono text-[10px] text-zinc-500 mt-1.5 block">
              Must include at least one word from your registered name: <span className="text-zinc-300">{p.name}</span>
            </span>
          </Field>

          <button
            type="submit"
            disabled={submitting || !allTicked || form.typed_name.trim().length < 2}
            data-testid="sign-submit"
            className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                SIGNING…
              </>
            ) : (
              <>
                <ShieldCheck className="w-3.5 h-3.5" />
                SIGN ELECTRONICALLY
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>

          <div className="font-mono text-[10px] text-zinc-500 leading-relaxed text-center pt-2">
            By submitting, we record your typed name, IP address, browser identifier, and timestamp as an audit trail.
            Both you and Dara receive a countersigned PDF by email.
          </div>
        </form>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> BACK TO HOMEPAGE
          </Link>
        </div>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">EXPERTS</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden sm:inline">
            <Lock className="w-3 h-3 inline mr-1 text-[#FFB000]" />
            SIGNING SESSION · PRIVATE
          </span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
    </div>
  );
}

function Pill({ label, value, accent = "#FFFFFF" }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-2.5">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-xs mt-0.5 truncate" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Affirm({ testid, checked, onChange, label }) {
  return (
    <label
      data-testid={testid}
      className={`flex items-start gap-3 cursor-pointer border p-3 transition-colors ${
        checked ? "border-[#FFB000] bg-[#FFB000]/[0.05]" : "border-zinc-800 hover:border-zinc-700 bg-black"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={`${testid}-checkbox`}
        className="mt-1 accent-[#FFB000] w-4 h-4 flex-shrink-0 cursor-pointer"
      />
      <span className="text-sm text-zinc-200 leading-relaxed">{label}</span>
    </label>
  );
}

const inputCls =
  "mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
